import { EventState, EventSubType, EventType, ExecuteEvent } from "pubsub/PubSub.const";
import IEvent from "./IEvent";
import { getInteractor } from "services/local.service";
import { refactorSavingEvent, updateRetryEvent } from "pubsub/PubSub.helper";
import { EventDto, IEventDto, IReceivedMember, ISeenMember } from "pubsub/dto";
import { IMessage } from "pubsub/dto/message.tb";
import { KeyConstant, SystemConstant } from "const";
import store, { ConversationActions } from "redux-store";
import { removeDuplicateInArray, StorageUtil } from "utils";
import { checkCurrentBranchByPrefix, checkTriggerMessageUI } from "sagas/saga.helper";
import { setUnreadInAppLogo } from "pubsub/services/message/getMessage";
import { updateDisappearTime } from "services/message.service";

// Update message status
export default class MessageStatusEvent extends IEvent {
  constructor(prefixKey: string) {
    super(prefixKey, EventType.MESSAGE_STATUS);
  }

  override handleEvent = async (limitRetry: number) => {
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, this.prefixKey);

    const eventList: EventDto[] = (
      await getInteractor(this.prefixKey).LocalEventService.find({
        type: this.eventType,
        state: [EventState.NEW, EventState.RESENT, EventState.TIMEOUT],
      })
    ).map((item: IEventDto) => new EventDto(item));
    const savingEvents: IEventDto[] = [];
    const savingSeenMembers: ISeenMember[] = [];
    const savingReceivedMembers: IReceivedMember[] = [];
    const savingMessages: { [x: string]: IMessage } = {};
    const updateMessageList: IMessage[] = [];
    const updateReadIds: string[] = [];

    for (let index = 0; index < eventList.length; index++) {
      const eventItem = eventList[index];
      const eventContent = eventItem.contentObj || {}; // {source_id, account_id}
      const isMine = eventContent.account_id === accountId; // this account seen|received message
      const isValidEvent = eventContent.source_id && eventContent.account_id;
      let executeEventValue: ExecuteEvent = ExecuteEvent.RETRY;

      // Skip loop event if it is not valid
      if (!isValidEvent) {
        const retryEvent = updateRetryEvent(eventItem, limitRetry);
        if (retryEvent) savingEvents.push(retryEvent);
        continue;
      }

      // Get message related event
      const existedMessage =
        (await getInteractor(this.prefixKey).LocalMessageService.findOne({
          source_id: eventContent.source_id,
          send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
          state: SystemConstant.STATE.active,
        })) || {};
      if (existedMessage?.id) {
        updateMessageList.push(existedMessage);
      }

      switch (eventItem.subtype) {
        case EventSubType.SEEN:
          savingSeenMembers.push({
            source_id: eventContent.source_id as string,
            member_account_id: eventContent.account_id as string,
            seen_at: eventItem.created,
          });

          // If login account's event and message existed => finish event + update local message db
          if (isMine && existedMessage.id) {
            executeEventValue = ExecuteEvent.SUCCESSES;
            updateReadIds.push(existedMessage.id);
            if (eventContent.source_id) {
              savingMessages[eventContent.source_id] = {
                source_id: eventContent.source_id,
                status: SystemConstant.MESSAGE_STATUS.read as number,
                modified: Date.now(),
              };
            }
          } else if (!isMine) {
            // If other account's event => finish event
            executeEventValue = ExecuteEvent.SUCCESSES;
          } else {
            executeEventValue = ExecuteEvent.RETRY;
          }
          break;

        case EventSubType.RECEIVED:
          savingReceivedMembers.push({
            source_id: eventContent.source_id as string,
            member_account_id: eventContent.account_id as string,
            received_at: eventItem.created,
          });

          // Handle login account's event
          if (isMine && existedMessage.id) {
            executeEventValue = ExecuteEvent.SUCCESSES;

            if (
              eventContent.source_id &&
              !savingMessages[eventContent.source_id] &&
              NOT_RECEIVED_STATUS.includes(existedMessage.status)
            ) {
              savingMessages[eventContent.source_id] = {
                source_id: eventContent.source_id as string,
                status: SystemConstant.MESSAGE_STATUS.received as number,
                modified: Date.now(),
              };
            }
          } else if (!isMine) {
            // If other account's event => finish event
            executeEventValue = ExecuteEvent.SUCCESSES;
          } else {
            executeEventValue = ExecuteEvent.RETRY;
          }

          break;

        default:
          // Failed event if not matching any subtype
          executeEventValue = ExecuteEvent.FAIL;
          break;
      }

      const refactorEvent = refactorSavingEvent(eventItem, executeEventValue, limitRetry);
      if (refactorEvent) savingEvents.push(refactorEvent);
    }

    // Save data in db
    await Promise.all([
      getInteractor(this.prefixKey).LocalSeenMemberService.save(savingSeenMembers),
      getInteractor(this.prefixKey).LocalReceivedMemberService.save(savingReceivedMembers),
      getInteractor(this.prefixKey).LocalEventService.save(savingEvents),
      getInteractor(this.prefixKey).LocalMessageService.updateList(Object.values(savingMessages)),
    ]);

    // Update unread in thread
    const updateThreadMessageList = updateMessageList.filter((item: IMessage) => item.thread_id);
    const updateSourceIdList = removeDuplicateInArray(updateThreadMessageList.map((item: IMessage) => item.thread_id));
    const updateParentMessageList = await getInteractor(this.prefixKey).LocalMessageService.find({
      source_id: updateSourceIdList,
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
      state: SystemConstant.STATE.active,
    });
    const updateThreadList = await Promise.all(
      updateSourceIdList.map(async (threadId: string) => {
        const totalUnread = await getInteractor(this.prefixKey).LocalMessageService.countUnreadMessageInThread(
          accountId,
          threadId,
        );

        return {
          total_unread: totalUnread,
          thread_id: threadId,
        };
      }),
    );
    await getInteractor(this.prefixKey).LocalThreadService.updateList(updateThreadList);
    if (updateThreadList.length > 0) {
      store.dispatch(
        ConversationActions.conversationSet({
          updateThreadStatus: {
            threadUpdate: updateThreadList,
            modified: Date.now(),
          },
        }),
      );
    }

    // Update unread in group
    if (updateReadIds.length > 0) {
      await getInteractor(this.prefixKey).LocalGroupService.updateUnread(removeDuplicateInArray(updateReadIds));
      await setUnreadInAppLogo();
      const isCurrentBranch = checkCurrentBranchByPrefix(this.prefixKey);
      if (isCurrentBranch) {
        store.dispatch(
          ConversationActions.conversationSet({
            isUpdateViewMode: Date.now(),
          }),
        );
      }

      // Update disappear_time
      // Trigger update disappearTime;
      updateDisappearTime(this.prefixKey, updateReadIds).then(minDisappearTime => {
        if (minDisappearTime > 0) {
          store.dispatch(
            ConversationActions.conversationSet({
              disappearTime: minDisappearTime,
            }),
          );
        }
      });
    }

    // Trigger to UI
    const messageList = removeDuplicateInArray([...updateMessageList, ...updateParentMessageList]);
    const isNeedTrigger = messageList.find(message => checkTriggerMessageUI(this.prefixKey, message));
    if (isNeedTrigger) {
      store.dispatch(
        ConversationActions.conversationSet({
          updateMessageStatus: {
            updateMessageIds: messageList.map((message: IMessage) => message.id),
            modified: Date.now(),
          },
        }),
      );
    }

    return true;
  };
}

const NOT_RECEIVED_STATUS = [SystemConstant.MESSAGE_STATUS.ready, SystemConstant.MESSAGE_STATUS.send];
